import { useParams, Link } from "react-router-dom";
import { useState, useReducer, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { isTossApp } from "@/lib/platform";
import { useLang } from "@/i18n/LangContext";
import { sanitizeHtml } from "@/lib/bbcode";
import { toSteamLang } from "@/lib/steam-locale";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import GameHeaderCard from "./game-detail/GameHeaderCard";
import GameInfoTable from "./game-detail/GameInfoTable";
import MediaSection from "./game-detail/MediaSection";
import GameStatsCards from "./game-detail/GameStatsCards";
import GameAnalysis from "./game-detail/GameAnalysis";
import HistoricLowCard from "./game-detail/HistoricLowCard";
import PriceHistoryChart from "./game-detail/PriceHistoryChart";
import PriceComparisonTable from "./game-detail/PriceComparisonTable";
import ReviewsSection from "./game-detail/ReviewsSection";
import AchievementsSection from "./game-detail/AchievementsSection";
import SimilarGamesCarousel from "./game-detail/SimilarGamesCarousel";
import SystemRequirements from "./game-detail/SystemRequirements";
import type {
  AppDetail,
  Review,
  ReviewSummary,
  Achievement,
  StoreLow,
  PriceDeal,
  HistoryPoint,
  TagData,
  DeckCompatItem,
  SimilarGame,
} from "@/types/api";

/* ─── Reducer ─── */

interface GameDetailState {
  app: AppDetail | null;
  loading: boolean;
  discountExpiration: number | null;
  playerCount: number | null;
  reviews: Review[];
  reviewSummary: ReviewSummary | null;
  reviewCursor: string;
  reviewsLoading: boolean;
  reviewsHasMore: boolean;
  reviewLang: string;
  achievements: Achievement[];
  totalAchievements: number;
  storeLow: StoreLow | null;
  historyLow: HistoryPoint[];
  priceDeals: PriceDeal[];
  tags: Record<string, number>;
  steamSpyOwners: string;
  steamSpyAvgPlaytime: number;
  similarGames: SimilarGame[];
  deckCompat: { category: number; items: DeckCompatItem[] } | null;
  freeGameInfo: { store: string; endDate?: string | null } | null;
}

type GameDetailAction =
  | { type: "SET_APP"; app: AppDetail }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_DISCOUNT_EXPIRATION"; value: number }
  | { type: "SET_PLAYER_COUNT"; value: number }
  | { type: "SET_REVIEWS"; reviews: Review[]; summary?: ReviewSummary; cursor?: string; hasMore?: boolean }
  | { type: "APPEND_REVIEWS"; reviews: Review[]; cursor?: string; hasMore: boolean }
  | { type: "RESET_REVIEWS" }
  | { type: "SET_REVIEWS_LOADING"; value: boolean }
  | { type: "SET_REVIEW_LANG"; value: string }
  | { type: "SET_ACHIEVEMENTS"; items: Achievement[]; total?: number }
  | { type: "SET_PRICE_DATA"; storeLow?: StoreLow | null; priceDeals?: PriceDeal[]; historyLow?: HistoryPoint[] }
  | { type: "SET_TAG_DATA"; tags?: Record<string, number>; owners?: string; avgPlaytime?: number }
  | { type: "SET_SIMILAR_GAMES"; games: SimilarGame[] }
  | { type: "SET_DECK_COMPAT"; data: GameDetailState["deckCompat"] }
  | { type: "SET_FREE_GAME_INFO"; info: GameDetailState["freeGameInfo"] };

function gameDetailReducer(state: GameDetailState, action: GameDetailAction): GameDetailState {
  switch (action.type) {
    case "SET_APP":
      return { ...state, app: action.app };
    case "SET_LOADING":
      return { ...state, loading: action.loading };
    case "SET_DISCOUNT_EXPIRATION":
      return { ...state, discountExpiration: action.value };
    case "SET_PLAYER_COUNT":
      return { ...state, playerCount: action.value };
    case "SET_REVIEWS":
      return {
        ...state,
        reviews: action.reviews,
        reviewSummary: action.summary ?? state.reviewSummary,
        reviewCursor: action.cursor ?? state.reviewCursor,
        reviewsHasMore: action.hasMore ?? state.reviewsHasMore,
      };
    case "APPEND_REVIEWS":
      return {
        ...state,
        reviews: [...state.reviews, ...action.reviews],
        reviewCursor: action.cursor ?? state.reviewCursor,
        reviewsHasMore: action.hasMore,
      };
    case "RESET_REVIEWS":
      return { ...state, reviews: [], reviewCursor: "*", reviewsHasMore: true };
    case "SET_REVIEWS_LOADING":
      return { ...state, reviewsLoading: action.value };
    case "SET_REVIEW_LANG":
      return { ...state, reviewLang: action.value };
    case "SET_ACHIEVEMENTS":
      return {
        ...state,
        achievements: action.items,
        totalAchievements: action.total ?? state.totalAchievements,
      };
    case "SET_PRICE_DATA":
      return {
        ...state,
        storeLow: action.storeLow !== undefined ? action.storeLow : state.storeLow,
        priceDeals: action.priceDeals ?? state.priceDeals,
        historyLow: action.historyLow ?? state.historyLow,
      };
    case "SET_TAG_DATA":
      return {
        ...state,
        tags: action.tags ?? state.tags,
        steamSpyOwners: action.owners ?? state.steamSpyOwners,
        steamSpyAvgPlaytime: action.avgPlaytime ?? state.steamSpyAvgPlaytime,
      };
    case "SET_SIMILAR_GAMES":
      return { ...state, similarGames: action.games };
    case "SET_DECK_COMPAT":
      return { ...state, deckCompat: action.data };
    case "SET_FREE_GAME_INFO":
      return { ...state, freeGameInfo: action.info };
    default:
      return state;
  }
}

/* ─── Countdown ─── */

function Countdown({ expiration }: { expiration: number }) {
  const { t } = useLang();
  const [remaining, setRemaining] = useState("");
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const update = () => {
      const tNow = tRef.current;
      const now = Math.floor(Date.now() / 1000);
      const diff = expiration - now;
      if (diff <= 0) {
        setRemaining(tNow.gameDetail.saleEnded);
        return;
      }
      const days = Math.floor(diff / 86400);
      const hours = Math.floor((diff % 86400) / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      if (days > 0) {
        setRemaining(`${days}${tNow.gameDetail.dayUnit} ${hours}${tNow.gameDetail.hourUnit} ${minutes}${tNow.gameDetail.minuteUnit}`);
      } else {
        setRemaining(
          `${hours}${tNow.gameDetail.hourUnit} ${minutes}${tNow.gameDetail.minuteUnit} ${seconds.toString().padStart(2, "0")}${tNow.gameDetail.secondUnit}`
        );
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiration]);

  const now = Math.floor(Date.now() / 1000);
  const diff = expiration - now;
  const isUrgent = diff > 0 && diff < 86400 * 2;

  return (
    <div
      className={`flex items-center justify-center gap-3 w-full px-6 py-4 rounded-2xl text-base font-bold shadow-md ${
        isUrgent
          ? "bg-rose-600 text-white shadow-rose-600/25 animate-pulse"
          : "bg-rose-500 text-white shadow-rose-500/25"
      }`}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span>{t.gameDetail.saleEndsIn} {remaining}</span>
    </div>
  );
}

/* ─── Main Component ─── */

interface AppDetailResponse {
  [key: string]: { success: boolean; data: AppDetail };
}

export default function GameDetailPage() {
  const { appId } = useParams<{ appId: string }>();
  const appid = appId || "";
  const { lang, t } = useLang();
  const [toss, setToss] = useState(false);

  useEffect(() => { setToss(isTossApp()); }, []);

  const [state, dispatch] = useReducer(gameDetailReducer, undefined, (): GameDetailState => ({
    app: null,
    loading: true,
    discountExpiration: null,
    playerCount: null,
    reviews: [],
    reviewSummary: null,
    reviewCursor: "*",
    reviewsLoading: false,
    reviewsHasMore: true,
    reviewLang: toSteamLang(lang),
    achievements: [],
    totalAchievements: 0,
    storeLow: null,
    historyLow: [],
    priceDeals: [],
    tags: {},
    steamSpyOwners: "",
    steamSpyAvgPlaytime: 0,
    similarGames: [],
    deckCompat: null,
    freeGameInfo: null,
  }));

  const {
    app, loading, discountExpiration, playerCount,
    reviews, reviewSummary, reviewCursor, reviewsLoading, reviewsHasMore, reviewLang,
    achievements, totalAchievements, storeLow, historyLow, priceDeals,
    tags, steamSpyOwners, steamSpyAvgPlaytime,
    similarGames, deckCompat, freeGameInfo,
  } = state;

  /* ── Data fetching ── */
  useEffect(() => {
    if (!appid) return;
    const controller = new AbortController();
    const { signal } = controller;
    let cancelled = false;
    const safeDispatch: typeof dispatch = (action) => {
      if (!cancelled) dispatch(action);
    };

    // App details
    const fetchDetail = async () => {
      try {
        const res = await fetch(api.store.appDetails(appid, lang), { signal });
        if (cancelled) return;
        const data: AppDetailResponse = await res.json();
        if (cancelled) return;
        const entry = data[appid];
        if (entry?.success) safeDispatch({ type: "SET_APP", app: entry.data });
      } catch { /* */ }
      safeDispatch({ type: "SET_LOADING", loading: false });

      // Featured categories for discount expiration
      try {
        const featuredRes = await fetch(api.store.featuredCategories(lang), { signal });
        if (cancelled) return;
        const featuredData = await featuredRes.json();
        if (cancelled) return;
        const categories = ["specials", "top_sellers", "new_releases", "coming_soon"];
        for (const cat of categories) {
          const items = featuredData[cat]?.items || [];
          const found = items.find((item: { id: number; discount_expiration?: number }) => String(item.id) === appid);
          if (found?.discount_expiration) {
            safeDispatch({ type: "SET_DISCOUNT_EXPIRATION", value: found.discount_expiration });
            break;
          }
        }
        for (const key of Object.keys(featuredData)) {
          if (/^\d+$/.test(key)) {
            const items = featuredData[key]?.items || [];
            const found = items.find((item: { id: number; discount_expiration?: number }) => String(item.id) === appid);
            if (found?.discount_expiration) {
              safeDispatch({ type: "SET_DISCOUNT_EXPIRATION", value: found.discount_expiration });
              break;
            }
          }
        }
      } catch { /* */ }
    };
    fetchDetail();

    // Parallel extra data
    const fetchExtra = async () => {
      const [playersRes, achievementsRes, priceRes, tagsRes] = await Promise.allSettled([
        fetch(api.store.players(appid), { signal }),
        fetch(api.store.achievements(appid, lang), { signal }),
        fetch(api.priceHistory(appid, lang) as unknown as string, { signal }),
        fetch(api.store.tags(appid, lang), { signal }),
      ]);
      if (cancelled) return;

      if (playersRes.status === "fulfilled") {
        try {
          const data = await playersRes.value.json();
          if (!cancelled && data.response?.player_count !== undefined) safeDispatch({ type: "SET_PLAYER_COUNT", value: data.response.player_count });
        } catch { /* */ }
      }

      if (achievementsRes.status === "fulfilled") {
        try {
          const data = await achievementsRes.value.json();
          if (cancelled) return;
          const items = data.achievementpercentages?.achievements || [];
          safeDispatch({ type: "SET_ACHIEVEMENTS", items: items.slice(0, 30), total: data.total });
        } catch { /* */ }
      }

      if (priceRes.status === "fulfilled") {
        try {
          const data = await priceRes.value.json();
          if (cancelled) return;
          let newStoreLow: StoreLow | null = null;
          let newPriceDeals: PriceDeal[] | undefined;
          let newHistoryLow: HistoryPoint[] | undefined;
          if (data.found && data.storeLow) {
            const lows = data.storeLow;
            if (Array.isArray(lows) && lows.length > 0 && lows[0].lows?.length > 0) {
              newStoreLow = [...lows[0].lows].sort((a: StoreLow, b: StoreLow) => a.price.amount - b.price.amount)[0];
            }
          }
          if (data.found && data.prices) {
            const prices = data.prices;
            if (Array.isArray(prices) && prices.length > 0 && prices[0].deals) {
              newPriceDeals = prices[0].deals;
            }
          }
          if (data.found && data.historyLow) {
            const history = data.historyLow;
            if (Array.isArray(history) && history.length > 0 && history[0].lows?.length > 0) {
              newHistoryLow = history[0].lows;
            }
          }
          safeDispatch({ type: "SET_PRICE_DATA", storeLow: newStoreLow, priceDeals: newPriceDeals, historyLow: newHistoryLow });
        } catch { /* */ }
      }

      if (tagsRes.status === "fulfilled") {
        try {
          const data: TagData = await tagsRes.value.json();
          if (cancelled) return;
          safeDispatch({
            type: "SET_TAG_DATA",
            tags: data.tags || undefined,
            owners: data.owners || undefined,
            avgPlaytime: data.average_forever || undefined,
          });
        } catch { /* */ }
      }

    };
    fetchExtra();

    // Similar games, guides, deck compat, bundles
    const fetchSimilar = async () => {
      try {
        const res = await fetch(api.store.similar(appid, lang), { signal });
        if (cancelled) return;
        const data = await res.json();
        if (!cancelled && data.results) safeDispatch({ type: "SET_SIMILAR_GAMES", games: data.results });
      } catch { /* */ }
    };
    const fetchDeckCompat = async () => {
      try {
        const res = await fetch(api.store.deckCompat(appid, lang), { signal });
        if (cancelled) return;
        const data = await res.json();
        if (!cancelled && data.category !== undefined) safeDispatch({ type: "SET_DECK_COMPAT", data });
      } catch { /* */ }
    };
    fetchSimilar();
    fetchDeckCompat();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [appid, lang]);

  // Free game check (Epic etc) — app.name이 처음 정해질 때 한 번만
  useEffect(() => {
    if (!app?.name) return;
    let cancelled = false;
    const appName = app.name;
    const check = async () => {
      try {
        const res = await fetch(api.freeGamesUrl());
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
        const gameName = normalize(appName);
        const allGames = [...(data.free || []), ...(data.upcoming || [])];
        const match = allGames.find((g: { title: string }) => normalize(g.title) === gameName);
        if (match && !cancelled) {
          const isFree = (data.free || []).some((g: { id: string }) => g.id === match.id);
          dispatch({
            type: "SET_FREE_GAME_INFO",
            info: {
              store: match.seller?.name || "Epic Games",
              endDate: isFree ? match.promoEndDate : null,
            },
          });
        }
      } catch { /* */ }
    };
    check();
    return () => { cancelled = true; };
  }, [app?.name]);

  // Reviews fetch (on language change) — mount 가드
  useEffect(() => {
    if (!appid) return;
    let cancelled = false;
    const fetchReviews = async () => {
      if (cancelled) return;
      dispatch({ type: "RESET_REVIEWS" });
      try {
        const [listRes, summaryRes] = await Promise.all([
          fetch(api.store.reviews({ appid, filter: "recent", num_per_page: 20, language: reviewLang, lang })),
          fetch(api.store.reviews({ appid, filter: "all", num_per_page: 0, language: reviewLang, lang })),
        ]);
        if (cancelled) return;
        const listData = await listRes.json();
        const summaryData = await summaryRes.json();
        if (cancelled) return;
        const summary = summaryData.query_summary ?? listData.query_summary;
        if (summary && listData.query_summary) {
          summary.num_reviews = listData.query_summary.total_reviews ?? 0;
        }
        dispatch({
          type: "SET_REVIEWS",
          reviews: listData.reviews || [],
          summary,
          cursor: listData.cursor,
          hasMore: (listData.reviews?.length ?? 0) >= 20,
        });
      } catch { /* */ }
    };
    fetchReviews();
    return () => { cancelled = true; };
  }, [appid, reviewLang, lang]);

  // Hide broken images in description — app.about_the_game/detailed_description 바뀔 때만
  const descriptionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!descriptionRef.current) return;
    const imgs = descriptionRef.current.querySelectorAll("img");
    const handleError = (e: Event) => { (e.target as HTMLElement).style.display = "none"; };
    imgs.forEach((img) => img.addEventListener("error", handleError));
    return () => imgs.forEach((img) => img.removeEventListener("error", handleError));
  }, [app?.about_the_game, app?.detailed_description, app?.short_description]);

  // Load more reviews + 컴포넌트 unmount 가드
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadMoreReviewsRef = useRef<() => void>(undefined);
  loadMoreReviewsRef.current = async () => {
    if (reviewsLoading || !reviewsHasMore || reviewCursor === "*") return;
    if (!mountedRef.current) return;
    dispatch({ type: "SET_REVIEWS_LOADING", value: true });
    try {
      const res = await fetch(api.store.reviews({ appid, filter: "recent", num_per_page: 20, cursor: reviewCursor, language: reviewLang, lang }));
      if (!mountedRef.current) return;
      if (!res.ok) {
        dispatch({ type: "APPEND_REVIEWS", reviews: [], hasMore: false });
        dispatch({ type: "SET_REVIEWS_LOADING", value: false });
        return;
      }
      const data = await res.json();
      if (!mountedRef.current) return;
      if (data.reviews && data.reviews.length > 0) {
        dispatch({ type: "APPEND_REVIEWS", reviews: data.reviews, cursor: data.cursor, hasMore: !!data.cursor && data.cursor !== "*" });
      } else {
        dispatch({ type: "APPEND_REVIEWS", reviews: [], hasMore: false });
      }
    } catch {
      if (mountedRef.current) dispatch({ type: "APPEND_REVIEWS", reviews: [], hasMore: false });
    }
    if (mountedRef.current) dispatch({ type: "SET_REVIEWS_LOADING", value: false });
  };

  // IntersectionObserver for infinite scroll
  const reviewsObserverRef = useRef<IntersectionObserver | null>(null);
  const reviewsSentinelCallback = useCallback((node: HTMLDivElement | null) => {
    if (reviewsObserverRef.current) {
      reviewsObserverRef.current.disconnect();
      reviewsObserverRef.current = null;
    }
    if (node) {
      reviewsObserverRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) loadMoreReviewsRef.current?.();
        },
        { threshold: 0.1 }
      );
      reviewsObserverRef.current.observe(node);
    }
  }, []);

  if (loading) return <Spinner />;

  if (!app) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-12 text-center">
        <p className="text-gray-500 dark:text-muted text-lg">{t.gameDetail.notFound}</p>
        <Link to="/deals" className="text-blue-500 hover:underline text-sm">{t.gameDetail.backToDealsLong}</Link>
      </div>
    );
  }

  const isMature = app.content_descriptors?.ids?.some((id) => id === 1 || id === 3 || id === 4) ?? false;

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Back — 토스에서는 네이티브 뒤로가기 사용 */}
      {!toss && (
        <Link
          to="/deals"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 dark:text-muted hover:text-gray-600 dark:hover:text-white transition-colors mb-4"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t.gameDetail.backToDealsLong}
        </Link>
      )}

      {/* Hero Image */}
      <div className="relative w-full aspect-[96/31] rounded-2xl overflow-hidden mb-6 bg-hover-bg dark:bg-background">
        <img
          src={`https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${appid}/library_hero.jpg`}
          alt={app.name}
          className={`w-full h-full object-cover${isMature ? " blur-xl" : ""}`}
          decoding="async"
          fetchPriority="high"
          onError={(e) => { (e.target as HTMLImageElement).src = app.header_image; }}
        />
        {isMature && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
            <span className="text-sm font-bold text-white bg-red-500/80 px-3 py-1 rounded-lg">19+</span>
          </div>
        )}
      </div>

      {/* Discount countdown */}
      {discountExpiration && discountExpiration > Math.floor(Date.now() / 1000) && (
        <div className="mb-6">
          <Countdown expiration={discountExpiration} />
        </div>
      )}

      {/* 1. Header Card */}
      <GameHeaderCard app={app} freeGameInfo={freeGameInfo} />

      {/* 2. Game Info Table */}
      <GameInfoTable app={app} />

      {/* 3. Media — Trailers + Screenshots */}
      <MediaSection app={app} isMature={isMature} />

      {/* 4. About — Game description HTML */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t.gameDetail.aboutGame}</h2>
        <div
          ref={descriptionRef}
          className={`text-sm text-gray-700 dark:text-foreground leading-7 break-words [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-3 [&_video]:max-w-full [&_video]:h-auto [&_video]:rounded-lg [&_video]:my-3${isMature ? " [&_img]:blur-xl [&_video]:blur-xl" : ""}`}
          dangerouslySetInnerHTML={{
            __html: (() => {
              let html = app.about_the_game || app.detailed_description || app.short_description;
              if (!html) return "";
              html = sanitizeHtml(html);
              if (isMature) {
                html = html.replace(/<video /gi, '<video style="filter:blur(24px)" ');
                html = html.replace(/<img /gi, '<img style="filter:blur(24px)" ');
              }
              return html;
            })(),
          }}
        />
      </div>

      {/* 5. Stats Cards — Players, Owners, Playtime, Deck, Tags */}
      <GameStatsCards
        playerCount={playerCount}
        steamSpyOwners={steamSpyOwners}
        steamSpyAvgPlaytime={steamSpyAvgPlaytime}
        deckCompat={deckCompat}
        tags={tags}
      />

      {/* 6. Analysis Report */}
      <GameAnalysis
        app={app}
        playerCount={playerCount}
        reviewSummary={reviewSummary}
        storeLow={storeLow}
        historyLow={historyLow}
        priceDeals={priceDeals}
        steamSpyOwners={steamSpyOwners}
        steamSpyAvgPlaytime={steamSpyAvgPlaytime}
        freeGameInfo={freeGameInfo}
      />

      {/* 7. Historic Low */}
      {storeLow && <HistoricLowCard storeLow={storeLow} />}

      {/* 8. Price History Chart */}
      {historyLow.length > 0 && (
        <PriceHistoryChart
          data={historyLow}
          currentPrice={
            priceDeals.length > 0
              ? [...priceDeals].sort((a, b) => a.price.amount - b.price.amount)[0].price.amount
              : undefined
          }
          currency={historyLow[0]?.price?.currency}
        />
      )}

      {/* 9. Price Comparison Table */}
      <PriceComparisonTable priceDeals={priceDeals} />


      {/* 11. Reviews */}
      <ReviewsSection
        appid={appid}
        appidNum={app.steam_appid}
        reviews={reviews}
        reviewSummary={reviewSummary}
        reviewLang={reviewLang}
        setReviewLang={(v) => dispatch({ type: "SET_REVIEW_LANG", value: v })}
        reviewsLoading={reviewsLoading}
        reviewsHasMore={reviewsHasMore}
        reviewsSentinelCallback={reviewsSentinelCallback}
        onLoadMore={() => loadMoreReviewsRef.current?.()}
      />

      {/* 12. Achievements */}
      <AchievementsSection appid={appid} achievements={achievements} totalAchievements={totalAchievements} />



      {/* 15. System Requirements */}
      {app.pc_requirements?.minimum && (
        <SystemRequirements minimum={app.pc_requirements.minimum} recommended={app.pc_requirements.recommended} />
      )}


      {/* 19. Similar Games */}
      <SimilarGamesCarousel similarGames={similarGames} />

      {/* Ad Banner */}
      <AdBanner className="mb-6" />

      <div className="pb-6" />
    </div>
  );
}
